import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatTrainingDuration, LearningTraining, TrainingCommentPath } from '../types';
import { listTrainingComments } from '../services/learningCenterService';
import TrainingComments from './TrainingComments';

export const LESSON_HOVER_OPEN_MS = 650;
const WATCH_COMPLETE_RATIO = 0.8;

export type LessonFeedItem = {
  training: LearningTraining;
  stepId: string;
  feedKey?: string;
  moduleTitle?: string;
  complete: boolean;
  commentPath?: TrainingCommentPath;
};

type LessonFeedPlayerProps = {
  items: LessonFeedItem[];
  startTrainingId: string;
  onClose: () => void;
  onWatched?: (stepId: string) => void;
};

const COMMENT_PROMPTS = [
  'Thoughts?!',
  'What stood out?',
  'Agree or disagree?',
  'Hot take?',
  'What would you add?',
  'Anyone else?',
  'First reaction?',
  'Worth a try?',
  'What am I missing?',
  'Say more.',
];

const promptFor = (id: string) => {
  const total = COMMENT_PROMPTS.length;
  const index = Array.from(id).reduce((sum, char) => sum + char.charCodeAt(0), 0) % total;
  return COMMENT_PROMPTS[index];
};

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const LessonSlide: React.FC<{
  item: LessonFeedItem;
  active: boolean;
  nearby: boolean;
  nativeControls: boolean;
  playbackRate: number;
  fast: boolean;
  onToggleFast: () => void;
  onWatched?: (stepId: string) => void;
  onEnded: () => void;
  onPlayingChange: (paused: boolean) => void;
  commentsOpen?: boolean;
  commentCount?: number;
  commentPrompt?: string;
  onToggleComments?: () => void;
}> = ({ item, active, nearby, nativeControls, playbackRate, fast, onToggleFast, onWatched, onEnded, onPlayingChange, commentsOpen, commentCount, commentPrompt, onToggleComments }) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const marked = useRef(item.complete);
  const [justCompleted, setJustCompleted] = useState(false);
  const [watched, setWatched] = useState(0);
  const { training } = item;

  useEffect(() => { marked.current = item.complete; }, [item.complete]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!active) {
      video.pause();
      return;
    }
    video.playbackRate = playbackRate;
    // Mobile browsers commonly reject unmuted programmatic playback even when
    // the feed was opened from a tap. Leave playback to the native control so
    // the subsequent play is a direct user gesture.
    if (nativeControls) {
      video.pause();
      onPlayingChange(true);
      return;
    }
    video.muted = false;
    const attempt = video.play();
    if (attempt) attempt.catch(() => onPlayingChange(true));
  }, [active, nativeControls, onPlayingChange, playbackRate, training.id]);

  useEffect(() => {
    const video = videoRef.current;
    if (video) video.playbackRate = playbackRate;
  }, [playbackRate, active, training.id]);

  const checkProgress = () => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    setWatched(video.currentTime / video.duration);
    if (marked.current) return;
    if (video.currentTime / video.duration >= WATCH_COMPLETE_RATIO) {
      marked.current = true;
      setJustCompleted(true);
      onWatched?.(item.stepId);
    }
  };

  return (
    <article className={`lesson-feed__slide ${active ? 'is-active' : ''}`} aria-hidden={!active}>
      <div className="lesson-feed__bar" aria-hidden="true"><i style={{ width: `${watched * 100}%` }} /></div>
      <div
        className="lesson-feed__stage"
        onClick={() => {
          if (nativeControls) return;
          const video = videoRef.current;
          if (!video) return;
          if (video.paused) { video.play().catch(() => undefined); onPlayingChange(false); }
          else { video.pause(); onPlayingChange(true); }
        }}
      >
        {training.video?.url && nearby ? (
          <video
            ref={videoRef}
            className="lesson-feed__video"
            src={training.video.url}
            poster={training.thumbnail?.url}
            playsInline
            preload="metadata"
            controls={nativeControls}
            onClick={event => nativeControls && event.stopPropagation()}
            onPlay={() => onPlayingChange(false)}
            onPause={() => onPlayingChange(true)}
            onTimeUpdate={checkProgress}
            onEnded={onEnded}
          />
        ) : training.video?.url && training.thumbnail?.url ? (
          <img className="lesson-feed__poster" src={training.thumbnail.url} alt="" />
        ) : !training.video?.url ? (
          <div className="lesson-feed__poster lesson-feed__poster--empty lesson-feed__video-missing" role="note">
            No tutorial video uploaded yet. Please read the notes for this part of the certification.
          </div>
        ) : (
          <div className="lesson-feed__poster lesson-feed__poster--empty" />
        )}
        {(onWatched && (item.complete || justCompleted)) && <span className="lesson-feed__complete">Complete</span>}
        {active && training.video?.url && (
          <button
            type="button"
            className={`lesson-feed__speed ${fast ? 'is-on' : ''}`}
            onClick={event => { event.stopPropagation(); onToggleFast(); }}
            aria-pressed={fast}
            aria-label={fast ? 'Play at normal speed' : 'Play at 1.5 times speed'}
          >
            <small>{fast ? 'Playing at' : 'Watch faster'}</small>
            1.5x
          </button>
        )}
        {active && item.commentPath && (
          <button
            type="button"
            className={`lesson-feed__speed lesson-feed__discuss ${commentsOpen ? 'is-on' : ''}`}
            onClick={event => { event.stopPropagation(); onToggleComments?.(); }}
            aria-pressed={Boolean(commentsOpen)}
          >
            <small>{commentCount ? `${commentCount} comments` : 'Click here to comment'}</small>
            {commentPrompt || 'Thoughts?!'}
          </button>
        )}
      </div>
      <div className="lesson-feed__copy">
        {item.moduleTitle && <span className="lesson-feed__module">{item.moduleTitle}</span>}
        <h2>{training.title}</h2>
        {training.description && <p>{training.description}</p>}
        {training.learningObjective && <p className="lesson-feed__objective">{training.learningObjective}</p>}
        {training.writtenContent && (
          <div className="lesson-feed__written" dangerouslySetInnerHTML={{ __html: training.writtenContent }} />
        )}
        <div className="lesson-feed__meta">
          <small>{formatTrainingDuration(training)}</small>
          {training.linkUrl && (
            <a href={training.linkUrl} target="_blank" rel="noreferrer" onClick={event => event.stopPropagation()}>
              {training.linkLabel || 'Open resource'} ↗
            </a>
          )}
          {(training.documents || []).map(file => (
            <a key={file.storagePath} href={file.url} target="_blank" rel="noreferrer" onClick={event => event.stopPropagation()}>
              {file.name} ↗
            </a>
          ))}
        </div>
      </div>
    </article>
  );
};

const LessonFeedPlayer: React.FC<LessonFeedPlayerProps> = ({ items, startTrainingId, onClose, onWatched }) => {
  const startIndex = Math.max(0, items.findIndex(item => (item.feedKey || item.training.id) === startTrainingId));
  const [index, setIndex] = useState(startIndex);
  const [fast, setFast] = useState(false);
  const [paused, setPaused] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [nativeControls, setNativeControls] = useState(() => window.matchMedia?.('(pointer: coarse)').matches ?? false);
  const [commentCount, setCommentCount] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const touchStart = useRef<number | null>(null);
  const wheelLock = useRef(false);
  const advanceLock = useRef(false);
  const indexRef = useRef(startIndex);
  indexRef.current = index;

  useEffect(() => {
    const query = window.matchMedia?.('(pointer: coarse)');
    if (!query) return;
    const update = () => setNativeControls(query.matches);
    update();
    query.addEventListener?.('change', update);
    return () => query.removeEventListener?.('change', update);
  }, []);

  const go = useCallback((delta: number) => {
    setIndex(current => clamp(current + delta, 0, Math.max(0, items.length - 1)));
    setPaused(false);
    setCommentCount(0);
  }, [items.length]);

  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    viewportRef.current?.focus();
    const node = viewportRef.current;
    const onNativeWheel = (event: WheelEvent) => {
      if ((event.target as HTMLElement).closest('.lesson-feed__copy, .lesson-feed__comments')) return;
      if (Math.abs(event.deltaY) < 18) return;
      event.preventDefault();
      if (wheelLock.current) return;
      wheelLock.current = true;
      go(event.deltaY > 0 ? 1 : -1);
      window.setTimeout(() => { wheelLock.current = false; }, 420);
    };
    node?.addEventListener('wheel', onNativeWheel, { passive: false });
    return () => {
      document.body.style.overflow = previous;
      node?.removeEventListener('wheel', onNativeWheel);
    };
  }, [go]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { onClose(); return; }
      if (event.key === 'ArrowDown' || event.key === 'ArrowRight') { event.preventDefault(); go(1); }
      if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') { event.preventDefault(); go(-1); }
      if (event.key === 's' || event.key === 'S') setFast(current => !current);
      if (event.key === ' ') {
        const target = event.target as HTMLElement | null;
        if (target && ['INPUT', 'TEXTAREA', 'BUTTON'].includes(target.tagName)) return;
        event.preventDefault();
        const video = viewportRef.current?.querySelector<HTMLVideoElement>('.lesson-feed__slide.is-active video');
        if (!video) return;
        if (video.paused) { video.play().catch(() => undefined); setPaused(false); }
        else { video.pause(); setPaused(true); }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [go, onClose]);

  const onEnded = () => {
    if (advanceLock.current || indexRef.current >= items.length - 1) return;
    advanceLock.current = true;
    window.setTimeout(() => {
      go(1);
      advanceLock.current = false;
    }, 280);
  };

  const current = items[index];
  const next = items[index + 1];
  useEffect(() => {
    if (!current?.commentPath) { setCommentCount(0); return; }
    let active = true;
    listTrainingComments(current.commentPath)
      .then(items => { if (active) setCommentCount(items.length); })
      .catch(() => { if (active) setCommentCount(0); });
    return () => { active = false; };
  }, [current?.commentPath?.collection, current?.commentPath?.id]);
  if (!current) return null;

  return createPortal(
    <div className="lesson-feed" role="presentation" onMouseDown={event => event.target === event.currentTarget && onClose()}>
      <div
        className={`lesson-feed__card ${commentsOpen && current.commentPath ? 'has-comments' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={current.training.title}
        ref={viewportRef}
        tabIndex={-1}
        onTouchStart={event => {
          if ((event.target as HTMLElement).closest('.lesson-feed__copy, .lesson-feed__comments, button, a')) {
            touchStart.current = null;
            return;
          }
          touchStart.current = event.changedTouches[0]?.clientY ?? null;
        }}
        onTouchEnd={event => {
          if (touchStart.current == null) return;
          const delta = touchStart.current - (event.changedTouches[0]?.clientY ?? touchStart.current);
          touchStart.current = null;
          if (Math.abs(delta) < 48) return;
          event.preventDefault();
          go(delta > 0 ? 1 : -1);
        }}
      >
        <header className="lesson-feed__chrome">
          <button type="button" className="lesson-feed__icon" onClick={onClose} aria-label="Close lesson feed">×</button>
          <div>
            <span>Lesson {index + 1} of {items.length}</span>
            <strong>{current.moduleTitle || 'Certification lesson'}</strong>
          </div>
          <span className="lesson-feed__icon-spacer" aria-hidden="true" />
        </header>
        <div className="lesson-feed__viewport">
          <div className="lesson-feed__track" style={{ transform: nativeControls ? 'none' : `translateY(-${index * 100}%)` }}>
            {(nativeControls ? [current] : items).map((item, itemIndex) => {
              const actualIndex = nativeControls ? index : itemIndex;
              return (
              <LessonSlide
                key={item.feedKey || item.training.id}
                item={item}
                active={actualIndex === index}
                nearby={nativeControls ? true : Math.abs(actualIndex - index) <= 1}
                nativeControls={nativeControls}
                playbackRate={fast ? 1.5 : 1}
                fast={fast}
                onToggleFast={() => setFast(current => !current)}
                onWatched={onWatched}
                onEnded={onEnded}
                onPlayingChange={setPaused}
                commentsOpen={commentsOpen}
                commentCount={commentCount}
                commentPrompt={promptFor(item.feedKey || item.training.id)}
                onToggleComments={() => setCommentsOpen(open => !open)}
              />
              );
            })}
          </div>
        </div>
        {commentsOpen && current.commentPath && <TrainingComments path={current.commentPath} prompt={promptFor(current.feedKey || current.training.id)} onCount={setCommentCount} />}
        {index > 0 && (
          <button type="button" className="lesson-feed__nudge lesson-feed__nudge--up" onClick={() => go(-1)} aria-label="Previous lesson">⌃</button>
        )}
        <button
          type="button"
          className="lesson-feed__nudge lesson-feed__nudge--down"
          onClick={() => next ? go(1) : onClose()}
          aria-label={next ? `Next lesson: ${next.training.title}` : 'Close lesson feed'}
        >
          <span>{next ? 'Next' : 'Done'}</span>
          {next ? <b>⌄</b> : <b>✓</b>}
        </button>
        {paused && <div className="lesson-feed__paused">Paused</div>}
      </div>
    </div>,
    document.body
  );
};

export default LessonFeedPlayer;
